import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  NOTIFICATION_TYPE,
} from '../../../constants/common'
import { useInitData, useTimeZone, useUserData } from '../../../hooks'
import { isCalendarAvailable } from '../../../utils/calendar'
import { NotificationInline } from '../../Notification'
import { Skeleton } from '../../Skeleton'
import Text from '../../Text'
import {
  QA_NAME_CALENDAR_DAY_LOAD_WARNING,
  QA_NAME_CALENDAR_DAY_LOAD_WARNING_LINE,
} from '../CalendarDayLoad.constants'
import { useCalendarRequestsDayLoad } from '../CalendarDayLoad.hooks'
import {
  formatCalendarDayLoadDate,
  getCalendarDayLoadCheckedDate,
  getCalendarDayLoadFullLoads,
  getCalendarDayLoadMovedDate,
  getCalendarDayLoadNextFreeDate,
  getCalendarDayLoadWarningLines,
} from '../CalendarDayLoad.utils'

import * as styles from './CalendarDayLoadWarning.module.css'

function CalendarDayLoadWarning({
  className = styles.CalendarDayLoadWarning,
  disabled,
  dueDate,
  requestIds,
  requests = EMPTY_LIST,
  value,
  onChangeCheck = EMPTY_FUNCTION,
  onSelectDate,
  ...readParams
}) {
  const { t } = useTranslation()

  const timezone = useTimeZone()
  const userData = useUserData()

  const { availableSections } = useInitData()

  const date = isCalendarAvailable(availableSections)
    ? getCalendarDayLoadCheckedDate({ dueDate, timezone, value })
    : null

  const { data, isFailed, isLoading } = useCalendarRequestsDayLoad({
    ...readParams,
    date,
    requestIds,
    requests,
  })

  const isPending = isLoading || isFailed

  useEffect(() => {
    onChangeCheck({ date, isPending, requestIds, requests })

    return () => {
      onChangeCheck(null)
    }
  }, [date, isPending])

  const fullLoads = getCalendarDayLoadFullLoads({ data, date })

  const nextFreeDate = getCalendarDayLoadNextFreeDate({
    data,
    date,
    fullLoads,
    user: userData,
  })

  const warningLines = getCalendarDayLoadWarningLines({
    data,
    date,
    fullLoads,
    t,
  })

  function handleSelectNextFreeDate() {
    onSelectDate(
      getCalendarDayLoadMovedDate({ date: nextFreeDate, timezone, value })
    )
  }

  return isLoading ? (
    <Skeleton Element='span' className={styles.skeleton} />
  ) : warningLines ? (
    <NotificationInline
      actions={
        nextFreeDate
          ? [
              {
                callback: handleSelectNextFreeDate,
                disabled,
                text: t('Calendar.CapMoveToDateKey', {
                  date: formatCalendarDayLoadDate(nextFreeDate),
                }),
              },
            ]
          : null
      }
      className={className}
      qaName={QA_NAME_CALENDAR_DAY_LOAD_WARNING}
      type={NOTIFICATION_TYPE.warning}
    >
      <div className={styles.lines}>
        {warningLines.map(line => (
          <Text
            className={styles.line}
            key={line.userId}
            qaName={QA_NAME_CALENDAR_DAY_LOAD_WARNING_LINE}
          >
            {line.text}
          </Text>
        ))}
      </div>
    </NotificationInline>
  ) : null
}

export default CalendarDayLoadWarning
